import { Button, Modal, useOverlayState } from '@heroui/react'
import { CheckCircle2, CircleAlert } from 'lucide-react'
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react'

type FeedbackTone = 'success' | 'error' | 'info'

interface ConfirmOptions {
  title: string
  description: string
  confirmLabel?: string
  tone?: 'danger' | 'primary'
}

interface AlertModalOptions {
  title: string
  description: string
  confirmLabel?: string
  tone?: 'primary' | 'danger'
}

interface FeedbackContextValue {
  notify: (message: string, tone?: FeedbackTone) => void
  confirm: (options: ConfirmOptions) => Promise<boolean>
  alertModal: (options: AlertModalOptions) => Promise<void>
}

interface Confirmation extends ConfirmOptions {
  isAlert?: boolean
  resolve: (confirmed: boolean) => void
}

export const FeedbackContext = createContext<FeedbackContextValue | null>(null)

export function FeedbackProvider({ children }: { children: ReactNode }) {
  const [message, setMessage] = useState<{ text: string; tone: FeedbackTone } | null>(null)
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null)
  const dismissTimeout = useRef<number | null>(null)
  const confirmationState = useOverlayState()

  const notify = useCallback((text: string, tone: FeedbackTone = 'info') => {
    if (dismissTimeout.current) window.clearTimeout(dismissTimeout.current)
    setMessage({ text, tone })
    dismissTimeout.current = window.setTimeout(() => setMessage(null), 5000)
  }, [])

  useEffect(() => {
    const nativeAlert = window.alert
    window.alert = (text?: string) => notify(String(text ?? ''), 'error')
    return () => { window.alert = nativeAlert }
  }, [notify])

  const confirm = useCallback((options: ConfirmOptions) => new Promise<boolean>((resolve) => {
    setConfirmation({ ...options, resolve })
    confirmationState.open()
  }), [confirmationState])

  const alertModal = useCallback((options: AlertModalOptions) => new Promise<void>((resolve) => {
    setConfirmation({
      ...options,
      isAlert: true,
      resolve: () => resolve(),
    })
    confirmationState.open()
  }), [confirmationState])

  const resolveConfirmation = (confirmed: boolean) => {
    confirmation?.resolve(confirmed)
    setConfirmation(null)
    confirmationState.close()
  }

  const colorClasses = message?.tone === 'error'
    ? 'border-red-200 bg-red-50 text-red-800 dark:border-red-900 dark:bg-red-950 dark:text-red-200'
    : message?.tone === 'success'
      ? 'border-emerald-200 bg-emerald-50 text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200'
      : 'border-teal-200 bg-teal-50 text-teal-800 dark:border-teal-900 dark:bg-teal-950 dark:text-teal-200'

  return (
    <FeedbackContext.Provider value={{ notify, confirm, alertModal }}>
      {children}
      {message && (
        <div role="status" aria-live="polite" data-testid="feedback-toast" className={`fixed right-4 top-4 z-[200] flex max-w-sm items-start gap-3 rounded-lg border px-4 py-3 text-sm font-semibold shadow-lg ${colorClasses}`}>
          {message.tone === 'error' ? <CircleAlert size={18} className="mt-0.5 shrink-0" /> : <CheckCircle2 size={18} className="mt-0.5 shrink-0" />}
          <p>{message.text}</p>
          <button type="button" aria-label="Tutup notifikasi" data-testid="feedback-toast-close-btn" onClick={() => setMessage(null)} className="ml-auto rounded p-0.5 focus-visible:outline-2 focus-visible:outline-current"><span aria-hidden="true">×</span></button>
        </div>
      )}
      {confirmationState.isOpen && (
        <Modal state={confirmationState}>
          <Modal.Backdrop isDismissable={false}>
            <Modal.Container>
              <Modal.Dialog data-testid="feedback-confirm-modal">
                <Modal.Header>
                  <div className="flex items-center gap-3">
                    {confirmation?.isAlert && (
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600 dark:bg-emerald-950 dark:text-emerald-400">
                        <CheckCircle2 size={20} />
                      </div>
                    )}
                    <Modal.Heading>{confirmation?.title}</Modal.Heading>
                  </div>
                </Modal.Header>
                <Modal.Body><p className="text-sm text-slate-600 dark:text-slate-300">{confirmation?.description}</p></Modal.Body>
                <Modal.Footer>
                  {!confirmation?.isAlert && (
                    <Button variant="secondary" onPress={() => resolveConfirmation(false)} data-testid="feedback-confirm-cancel-btn">Batal</Button>
                  )}
                  <Button variant={confirmation?.tone ?? 'primary'} onPress={() => resolveConfirmation(true)} data-testid="feedback-confirm-submit-btn">
                    {confirmation?.confirmLabel ?? (confirmation?.isAlert ? 'Tutup' : 'Lanjutkan')}
                  </Button>
                </Modal.Footer>
              </Modal.Dialog>
            </Modal.Container>
          </Modal.Backdrop>
        </Modal>
      )}
    </FeedbackContext.Provider>
  )
}

export function useFeedback() {
  const context = useContext(FeedbackContext)
  return (
    context ?? {
      notify: () => {},
      confirm: async ({ title }: { title: string }) => window.confirm(title),
      alertModal: async ({ title, description }: AlertModalOptions) => {
        window.alert(`${title}\n${description}`)
      },
    }
  )
}
